import { useState, useEffect, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { PageContainer, DataTable, Button } from "components/ui";

const statusBadge = (status) => {
  const map = {
    PRESENT:  { bg: "rgba(16,185,129,0.15)",  color: "#10b981", label: "Present"  },
    LATE:     { bg: "rgba(245,158,11,0.15)",  color: "#f59e0b", label: "Late"     },
    ABSENT:   { bg: "rgba(239,68,68,0.15)",   color: "#ef4444", label: "Absent"   },
    ON_LEAVE: { bg: "rgba(99,102,241,0.15)",  color: "#6366f1", label: "On leave" },
  };
  const s = map[status] || { bg: "rgba(148,163,184,0.15)", color: "#94a3b8", label: status };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
    }}>
      {s.label}
    </span>
  );
};

const fmt = (dt) => {
  if (!dt) return "—";
  return dt.slice(11, 16);
};

const duration = (clockIn, clockOut) => {
  if (!clockIn || !clockOut) return "—";
  const diff = new Date(clockOut) - new Date(clockIn);
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
};

const today = new Date().toISOString().slice(0, 10);

const AttendanceOverview = () => {
  const axiosPrivate = useAxiosPrivate();
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [dateFilter, setDateFilter] = useState(today);

  const size = 15;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get(
        `/api/rh/attendance?date=${dateFilter}&page=${page}&size=${size}`
      );
      setRecords(res.data.content);
      setTotalPages(res.data.page.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, dateFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Reset to page 0 when date changes
  const handleDateChange = (e) => {
    setPage(0);
    setDateFilter(e.target.value);
  };

  return (
    <PageContainer>
      <DataTable
        title="Attendance"
        subtitle={`Records for ${dateFilter}`}
        loading={loading}
        data={records}
        rowKey={(r) => r.id}
        search={true}
        searchPlaceholder="Search by employee name…"
        paginate={false}
        emptyTitle="No attendance records"
        emptySubtitle="No clock-ins recorded for this date."
        toolbar={
          <input
            type="date"
            value={dateFilter}
            onChange={handleDateChange}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8, color: "inherit",
              padding: "6px 12px", fontSize: 13,
              outline: "none", cursor: "pointer",
            }}
          />
        }
        columns={[
          {
            key: "employee",
            header: "Employee",
            render: (r) => (
              <span style={{ fontWeight: 500 }}>{r.employeeName}</span>
            ),
          },
          {
            key: "clockIn",
            header: "Clock in",
            render: (r) => <span className="t-mono">{fmt(r.clockIn)}</span>,
          },
          {
            key: "clockOut",
            header: "Clock out",
            render: (r) => <span className="t-mono">{fmt(r.clockOut)}</span>,
          },
          {
            key: "duration",
            header: "Duration",
            render: (r) => (
              <span className="t-mono t-muted">{duration(r.clockIn, r.clockOut)}</span>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (r) => statusBadge(r.status),
          },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>Page <strong>{page + 1}</strong> of {totalPages}</span>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button
                  variant="ghost" size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Prev
                </Button>
                <Button
                  variant="ghost" size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next ›
                </Button>
              </div>
            </div>
          ) : null
        }
      />
    </PageContainer>
  );
};

export default AttendanceOverview;